/** @jsx jsx */
import { useCallback } from 'react';
import { jsx, css } from '@compiled/react';
import { Inline, Stack, xcss, Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	SOFTWARE_PROJECT,
	CORE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { THIRD_PARTY_SOURCE_SLACK } from '@atlassian/jira-issue-view-common-types/src/comment-type.tsx';
import AkCommentAuthorWrapper from './AkCommentAuthorWrapper.tsx';
import CommentUnreadIndicator from './CommentUnreadIndicator.tsx';
import CommentTime from './CommentTime.tsx';
import CommentLozenge from './CommentLozenge.tsx';
import CommentAuthorActivity from './CommentAuthorActivity.tsx';
import CommentRestrictedToIndicator from './CommentRestrictedToIndicator.tsx';
import CommentPinIndicator from './CommentPinIndicator.tsx';
import CommentCopyLink from './CommentCopyLink.tsx';
import CommentEditedIndicator from './CommentEditedIndicator.tsx';
import CommentDeletedIndicator from './CommentDeletedIndicator.tsx';
import CommentSavingIndicator from './CommentSavingIndicator.tsx';
import { useCommentContext } from './CommentContextProvider.tsx';
import { SlackTooltip } from './slack-tooltip/SlackTooltip.tsx';
import SlackLink from './SlackLink.tsx';

const AKCommentCustomHeader = () => {
	const {
		hasFloatingMenu,
		floatingMenu,
		showLozenge,
		isDeleted,
		parentId,
		projectType,
		isThirdPartySyncOn,
		thirdPartyLink,
		thirdPartyCommentSource,
	} = useCommentContext();

	const getTooltip = useCallback(() => {
		// TODO: add comment slack-sync status prop check at beginning of condition
		if (
			!fg('jira_issue_view_show_slack_sync_info') ||
			(projectType !== SOFTWARE_PROJECT && projectType !== CORE_PROJECT) ||
			!isThirdPartySyncOn ||
			parentId ||
			thirdPartyCommentSource !== THIRD_PARTY_SOURCE_SLACK
		) {
			return null;
		}
		return <SlackTooltip href={thirdPartyLink} />;
	}, [projectType, isThirdPartySyncOn, parentId, thirdPartyCommentSource, thirdPartyLink]);

	if (isDeleted) {
		return <CommentDeletedIndicator />;
	}
	return (
		<Stack grow="fill" space="space.050" xcss={customHeaderStyles}>
			<Stack>
				<Inline space="space.050" alignBlock="center" grow="fill">
					<Box
						xcss={[truncatedStyles, fg('jira_issue_view_show_slack_sync_info') && withSlackIcons]}
					>
						<div css={primaryRowStyles}>
							<AkCommentAuthorWrapper />
						</div>
						{getTooltip()}
					</Box>
					<Box xcss={nonTruncatedStyles}>
						<div css={primaryRowStyles}>
							<Inline space="space.100" alignBlock="center" grow="fill">
								<CommentAuthorActivity />
								<Inline space="space.075" alignBlock="center">
									<CommentPinIndicator />
									<CommentRestrictedToIndicator />
									<CommentUnreadIndicator />
									<Box paddingInlineStart="space.050" paddingBlockStart="space.050">
										<CommentCopyLink />
									</Box>
									{hasFloatingMenu && floatingMenu}
								</Inline>
							</Inline>
						</div>
					</Box>
				</Inline>
				<Inline space="space.050" alignBlock="start">
					<div css={secondaryRowStyles}>
						<CommentTime />
					</div>
					<SlackLink />
					<div css={secondaryRowStyles}>
						<CommentEditedIndicator />
					</div>
				</Inline>
				<Inline space="space.050" alignBlock="start">
					<CommentSavingIndicator />
				</Inline>
			</Stack>
			{showLozenge && (
				<Box paddingBlock="space.075">
					<Inline space="space.050" alignBlock="center">
						<CommentLozenge />
					</Inline>
				</Box>
			)}
		</Stack>
	);
};
export default AKCommentCustomHeader;

const customHeaderStyles = xcss({
	overflow: 'hidden',
	paddingBlockEnd: 'space.025',
});
const primaryRowStyles = css({
	font: token('font.heading.xsmall'),
});
const secondaryRowStyles = css({
	font: token('font.body.small'),
});
const truncatedStyles = xcss({
	minWidth: '0px',
});
const nonTruncatedStyles = xcss({
	flexShrink: 0,
});
const withSlackIcons = xcss({
	display: 'flex',
	alignItems: 'center',
});
